/**
 * Cuvée Quiz — vanilla JS state machine.
 * Replaces the old Fyrebox embed at /pages/cuvee-quiz.
 *
 * Flow: intro → questions[0..n] → email gate → result reveal
 * Result: scoring engine ranks products by quiz answers, returns 3-5 SKUs.
 * Email capture: pushes to Klaviyo via window._learnq (and fallback to fetch).
 * Cart drop: POSTs each variant to /cart/add.js then redirects to /cart.
 *
 * (2026-05-03)
 */
(function () {
  'use strict';

  const root = document.querySelector('[data-cuvee-quiz]');
  if (!root) return;

  // ----- Telemetry (dual-fire to Klaviyo + GA4/GTM) -----
  // Build a stable session id so we can stitch events into a funnel
  const sessionId = (function () {
    const k = 'cuvee_quiz_session';
    try {
      let s = sessionStorage.getItem(k);
      if (!s) {
        s = 'q_' + Date.now() + '_' + Math.random().toString(36).slice(2, 8);
        sessionStorage.setItem(k, s);
      }
      return s;
    } catch (_) { return 'q_' + Date.now(); }
  })();
  const sessionStart = Date.now();

  function track(eventName, props) {
    const payload = {
      quiz: 'Cuvée Routine Quiz',
      session_id: sessionId,
      session_elapsed_ms: Date.now() - sessionStart,
      ...(props || {}),
    };
    // 1. Klaviyo client-side track (works only when klaviyo.js is loaded sitewide)
    try {
      if (window._learnq && Array.isArray(window._learnq)) {
        window._learnq.push(['track', eventName, payload]);
      }
    } catch (e) {}
    // 2. GA4 / GTM dataLayer (snake_case event names per GA4 convention)
    try {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: 'cuvee_quiz_' + eventName.toLowerCase().replace(/\s+/g, '_'),
        cuvee_quiz: payload,
      });
    } catch (e) {}
    // 3. Direct gtag (in case dataLayer isn't wired through GTM to GA4)
    try {
      if (typeof window.gtag === 'function') {
        window.gtag('event', 'cuvee_quiz_' + eventName.toLowerCase().replace(/\s+/g, '_'), payload);
      }
    } catch (e) {}
    // 4. Console for dev visibility
    try {
      if (window.location.search.includes('quiz_debug')) console.log('[cuvee-quiz]', eventName, payload);
    } catch (e) {}
  }

  // ----- Load configs -----
  const cfg = JSON.parse(root.parentElement.querySelector('[data-cuvee-quiz-config]').textContent);
  const catalog = JSON.parse(root.parentElement.querySelector('[data-cuvee-quiz-products]').textContent);
  const productsByHandle = Object.fromEntries(catalog.products.map(p => [p.handle, p]));

  // ----- State -----
  const state = {
    step: 'intro', // intro | question:N | email | result
    answers: {},
    qIndex: 0,
    email: null,
    firstName: null,
  };

  // ----- DOM refs -----
  const screens = {
    intro:    root.querySelector('[data-screen="intro"]'),
    question: root.querySelector('[data-screen="question"]'),
    email:    root.querySelector('[data-screen="email"]'),
    result:   root.querySelector('[data-screen="result"]'),
  };
  const elQTitle = root.querySelector('[data-q-title]');
  const elQHelp = root.querySelector('[data-q-help]');
  const elOptions = root.querySelector('[data-q-options]');
  const elProgress = root.querySelector('[data-progress]');
  const elBackQuestion = screens.question.querySelector('[data-action="back"]');
  const elBackEmail = screens.email.querySelector('[data-action="back"]');
  const elForm = root.querySelector('[data-quiz-form]');
  const elFormErr = root.querySelector('[data-form-error]');
  const elResultProducts = root.querySelector('[data-result-products]');
  const elResultEyebrow = root.querySelector('[data-result-eyebrow]');
  const elResultTitle = root.querySelector('[data-result-title]');
  const elResultSub = root.querySelector('[data-result-subtitle]');
  const elResultTotal = root.querySelector('[data-result-total]');

  // ----- Render helpers -----
  function showScreen(name) {
    const previous = state.step;
    Object.entries(screens).forEach(([k, el]) => {
      el.hidden = k !== name;
    });
    state.step = name;
    // Scroll to top of quiz on screen change
    if (root.getBoundingClientRect().top < 0) {
      root.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
    // Fire screen-view events
    if (name === 'email' && previous !== 'email') {
      track('Email Gate Viewed', { answers: { ...state.answers } });
    }
    if (name === 'result' && previous !== 'result') {
      track('Result Viewed', {
        answers: { ...state.answers },
        recommended_routine: (state.routine || []).join(','),
        recommended_count: (state.routine || []).length,
      });
    }
  }

  function renderProgress() {
    const total = cfg.questions.length;
    const dots = [];
    for (let i = 0; i < total; i++) {
      let cls = 'cuvee-quiz__progress-dot';
      if (i < state.qIndex) cls += ' cuvee-quiz__progress-dot--done';
      else if (i === state.qIndex) cls += ' cuvee-quiz__progress-dot--active';
      dots.push(`<span class="${cls}" aria-hidden="true"></span>`);
    }
    elProgress.innerHTML = dots.join('');
    elProgress.setAttribute('aria-label', `Question ${state.qIndex + 1} of ${total}`);
  }

  function renderQuestion() {
    const q = cfg.questions[state.qIndex];
    if (!q) {
      // No more questions → email gate
      showScreen('email');
      return;
    }
    renderProgress();
    elQTitle.textContent = q.title;
    if (q.help) {
      elQHelp.textContent = q.help;
      elQHelp.hidden = false;
    } else {
      elQHelp.hidden = true;
    }
    // Options
    const cols = q.options.length >= 6 ? 2 : 1;
    elOptions.setAttribute('data-cols', cols);
    elOptions.innerHTML = q.options.map((opt) => {
      const selected = state.answers[q.id] === opt.value ? 'cuvee-quiz__option--selected' : '';
      return `<li><button type="button" class="cuvee-quiz__option ${selected}" data-q-id="${q.id}" data-q-value="${opt.value}">${escapeHTML(opt.label)}</button></li>`;
    }).join('');
    elBackQuestion.hidden = state.qIndex === 0;
    showScreen('question');
  }

  function escapeHTML(s) {
    return String(s).replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  }

  // ----- Scoring engine -----
  function scoreProducts() {
    const scores = {};
    Object.entries(cfg.scoring).forEach(([handle, conf]) => {
      let score = 0;
      if (conf.scores) {
        Object.entries(conf.scores).forEach(([qid, weights]) => {
          const ans = state.answers[qid];
          if (ans && weights[ans]) score += weights[ans];
        });
      }
      scores[handle] = { score, role: conf.role, always: conf.always_include === true };
    });
    return scores;
  }

  function pickRoutine() {
    const scored = scoreProducts();
    const targetSize = cfg.routine_size[state.answers.styling_method] || cfg.routine_size[state.answers.salon_services] || cfg.routine_size.default;

    // Always-include core (Shampoo, Conditioner)
    const core = Object.entries(scored)
      .filter(([, v]) => v.always)
      .map(([h]) => h);

    // Top styling and treatment by score
    const styling = Object.entries(scored)
      .filter(([, v]) => v.role === 'styling' && v.score > 0)
      .sort((a, b) => b[1].score - a[1].score)
      .map(([h]) => h);
    const treatment = Object.entries(scored)
      .filter(([, v]) => v.role === 'treatment' && v.score > 0)
      .sort((a, b) => b[1].score - a[1].score)
      .map(([h]) => h);

    const result = [...core];
    // Fill remaining slots — alternate styling first, then treatment
    let s = 0, t = 0;
    while (result.length < targetSize) {
      if (s < styling.length) {
        if (!result.includes(styling[s])) result.push(styling[s]);
        s++;
      } else if (t < treatment.length) {
        if (!result.includes(treatment[t])) result.push(treatment[t]);
        t++;
      } else {
        break;
      }
    }
    // Filter to actually-existing products in the catalog
    return result.filter(h => productsByHandle[h]).slice(0, targetSize);
  }

  function renderResult() {
    const handles = pickRoutine();
    let totalCents = 0;
    elResultProducts.innerHTML = handles.map(h => {
      const p = productsByHandle[h];
      const conf = cfg.scoring[h] || {};
      totalCents += Number(p.price_cents) || 0;
      const roleLabel = conf.always_include ? 'Foundation' : (conf.role || '').replace(/^./, c => c.toUpperCase());
      return `
        <article class="cuvee-quiz__product" data-handle="${p.handle}" data-variant-id="${p.variant_id}">
          <img class="cuvee-quiz__product-img" src="${p.image}" alt="" loading="lazy">
          <p class="cuvee-quiz__product-role">${escapeHTML(roleLabel)}</p>
          <h3 class="cuvee-quiz__product-title"><a href="${p.url}">${escapeHTML(p.title)}</a></h3>
          <p class="cuvee-quiz__product-price">${escapeHTML(p.price)}</p>
        </article>
      `;
    }).join('');
    elResultTotal.textContent = formatMoney(totalCents);

    // Result eyebrow + headline can be personalized
    const concern = state.answers.top_concern;
    const goal = state.answers.hair_goal;
    elResultEyebrow.textContent = state.firstName ? `${state.firstName.toUpperCase()}, your routine is ready` : 'Your personalized routine';
    if (goal === 'celebration') {
      elResultTitle.textContent = 'Hair worth celebrating 🥂';
    } else if (concern === 'scalp') {
      elResultTitle.textContent = 'A scalp-first routine';
    } else if (concern === 'breakage' || concern === 'color_fade') {
      elResultTitle.textContent = 'A bond-protecting routine';
    } else {
      elResultTitle.textContent = `Your Cuvée routine — ${handles.length} products`;
    }

    // Save handles to state for cart drop
    state.routine = handles;
    showScreen('result');

    // Update URL hash so result is shareable
    try {
      const params = new URLSearchParams();
      Object.entries(state.answers).forEach(([k, v]) => params.set(k, v));
      params.set('result', handles.join(','));
      history.replaceState(null, '', '#' + params.toString());
    } catch (_) {}
  }

  function formatMoney(cents) {
    const amt = (cents / 100).toFixed(2);
    return '$' + amt;
  }

  // ----- Klaviyo / email submission -----
  async function submitEmail(email, firstName) {
    const props = {
      $email: email,
      ...(firstName ? { $first_name: firstName } : {}),
      cuvee_quiz_completed: true,
      cuvee_quiz_completed_at: new Date().toISOString(),
      cuvee_quiz_recommended_routine: pickRoutine().join(','),
      ...Object.fromEntries(Object.entries(state.answers).map(([k, v]) => [`cuvee_quiz_${k}`, v])),
    };

    // 1. Klaviyo client-side identify (works if klaviyo.js is loaded sitewide via app embed)
    if (window._learnq && Array.isArray(window._learnq)) {
      try {
        window._learnq.push(['identify', props]);
        window._learnq.push(['track', 'Completed Quiz', {
          quiz: 'Cuvée Routine Quiz',
          ...Object.fromEntries(Object.entries(state.answers).map(([k, v]) => [k, v])),
          recommended_routine: pickRoutine().join(','),
        }]);
      } catch (e) {
        console.warn('[cuvee-quiz] Klaviyo _learnq failed:', e);
      }
    }

    // 2. Shopify customer-newsletter signup as a fallback so the email is captured
    //    even if Klaviyo isn't reachable.
    try {
      const fd = new FormData();
      fd.append('form_type', 'customer');
      fd.append('utf8', '✓');
      fd.append('contact[email]', email);
      fd.append('contact[tags]', 'newsletter,cuvee-quiz');
      if (firstName) fd.append('contact[first_name]', firstName);
      await fetch('/contact#contact_form', { method: 'POST', body: fd });
    } catch (e) {
      console.warn('[cuvee-quiz] Shopify newsletter signup failed:', e);
    }

    return true;
  }

  // ----- Cart drop -----
  async function addRoutineToCart() {
    const handles = state.routine || pickRoutine();
    const items = handles
      .map(h => productsByHandle[h])
      .filter(p => p && p.available && p.variant_id);

    if (!items.length) {
      window.location.href = '/collections/all';
      return;
    }

    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({
          items: items.map(p => ({ id: p.variant_id, quantity: 1 })),
        }),
      });
      if (!res.ok) {
        const txt = await res.text();
        console.warn('[cuvee-quiz] /cart/add.js failed:', res.status, txt);
      }
    } catch (e) {
      console.warn('[cuvee-quiz] cart add failed:', e);
    }
    // Notify rest of theme + go to cart
    document.dispatchEvent(new Event('cuvee:cart:updated'));
    window.location.href = '/cart';
  }

  // ----- Event wiring -----
  let lastQuestionShownAt = Date.now();
  root.addEventListener('click', (e) => {
    const optBtn = e.target.closest('[data-q-id]');
    if (optBtn) {
      const qid = optBtn.dataset.qId;
      const val = optBtn.dataset.qValue;
      const previousAnswer = state.answers[qid];
      state.answers[qid] = val;
      // Telemetry — answer event
      track('Question Answered', {
        question_id: qid,
        question_index: state.qIndex,
        answer_value: val,
        is_change: previousAnswer && previousAnswer !== val,
        time_on_question_ms: Date.now() - lastQuestionShownAt,
      });
      // Auto-advance after a short visual confirmation delay
      optBtn.classList.add('cuvee-quiz__option--selected');
      setTimeout(() => {
        state.qIndex++;
        renderQuestion();
      }, 220);
      return;
    }
    const action = e.target.closest('[data-action]')?.dataset.action;
    if (!action) return;
    switch (action) {
      case 'start':
        track('Started');
        state.qIndex = 0;
        renderQuestion();
        break;
      case 'back':
        track('Back Clicked', {
          from_step: state.step,
          from_question_index: state.step === 'question' ? state.qIndex : null,
        });
        if (state.step === 'email') {
          state.qIndex = cfg.questions.length - 1;
          renderQuestion();
        } else if (state.step === 'question') {
          state.qIndex = Math.max(0, state.qIndex - 1);
          renderQuestion();
        }
        break;
      case 'restart':
        track('Restarted', {
          from_step: state.step,
          completed_answers: Object.keys(state.answers).length,
        });
        state.answers = {};
        state.qIndex = 0;
        history.replaceState(null, '', '#');
        showScreen('intro');
        break;
      case 'add-routine':
        track('Routine Added To Cart', {
          recommended_routine: (state.routine || pickRoutine()).join(','),
          email_captured: !!state.email,
        });
        const btn = e.target.closest('button');
        if (btn) {
          btn.disabled = true;
          btn.textContent = 'Adding...';
        }
        addRoutineToCart();
        break;
    }
  });

  // Result-product clicks (links to PDPs from result screen)
  root.addEventListener('click', (e) => {
    const link = e.target.closest('.cuvee-quiz__product a');
    if (!link) return;
    const productEl = link.closest('.cuvee-quiz__product');
    track('Result Product Clicked', {
      product_handle: productEl?.dataset.handle,
      destination: link.getAttribute('href'),
    });
  });

  // Time-on-question tracker — reset when a new question renders
  const _renderQuestion = renderQuestion;
  renderQuestion = function () {
    lastQuestionShownAt = Date.now();
    return _renderQuestion.apply(this, arguments);
  };

  elForm.addEventListener('submit', async (e) => {
    e.preventDefault();
    const fd = new FormData(elForm);
    const email = (fd.get('email') || '').toString().trim();
    const firstName = (fd.get('first_name') || '').toString().trim();
    elFormErr.hidden = true;
    if (!email || !/.+@.+\..+/.test(email)) {
      elFormErr.textContent = 'Please enter a valid email address.';
      elFormErr.hidden = false;
      track('Email Validation Failed', { reason: 'invalid_format' });
      return;
    }
    state.email = email;
    state.firstName = firstName;
    track('Email Submitted', {
      first_name_provided: !!firstName,
      total_time_ms: Date.now() - sessionStart,
    });
    const submitBtn = elForm.querySelector('button[type="submit"]');
    submitBtn.disabled = true;
    submitBtn.textContent = 'Loading your routine...';
    try {
      await submitEmail(email, firstName);
      track('Quiz Completed', {
        email_provided: true,
        recommended_routine: pickRoutine().join(','),
        answers: { ...state.answers },
      });
    } catch (e) {
      console.warn('[cuvee-quiz] email submit error', e);
    }
    submitBtn.disabled = false;
    submitBtn.textContent = 'Reveal my routine';
    renderResult();
  });

  // Page-unload: fire abandonment event if quiz wasn't completed
  window.addEventListener('beforeunload', () => {
    if (state.step !== 'result' && Object.keys(state.answers).length > 0) {
      try {
        // Use Beacon API (survives page unload)
        const data = new Blob([JSON.stringify({
          event: 'cuvee_quiz_abandoned',
          cuvee_quiz: {
            quiz: 'Cuvée Routine Quiz',
            session_id: sessionId,
            from_step: state.step,
            from_question_index: state.qIndex,
            completed_answers: Object.keys(state.answers).length,
            answers: { ...state.answers },
          },
        })], { type: 'application/json' });
        // Fire-and-forget to dataLayer (synchronous)
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
          event: 'cuvee_quiz_abandoned',
          cuvee_quiz: {
            session_id: sessionId,
            from_step: state.step,
            from_question_index: state.qIndex,
            completed_answers: Object.keys(state.answers).length,
          },
        });
      } catch (e) {}
    }
  });

  // ----- Boot -----
  // If URL hash has answers (shareable result), restore state
  try {
    if (location.hash.length > 1) {
      const params = new URLSearchParams(location.hash.slice(1));
      let restored = false;
      cfg.questions.forEach(q => {
        const v = params.get(q.id);
        if (v) {
          state.answers[q.id] = v;
          restored = true;
        }
      });
      if (restored && params.get('result')) {
        // Skip directly to result if we have all answers
        const have = cfg.questions.every(q => state.answers[q.id]);
        if (have) {
          state.qIndex = cfg.questions.length;
          renderResult();
          return;
        }
      }
    }
  } catch (_) {}

  // Fire "Quiz Page Loaded" so we can measure intro impression vs Started CTR
  track('Page Loaded');

  showScreen('intro');
})();
